/* Add and edit, for every built-in register.
 *
 * Until this existed the app could read six registers and populate none of
 * them — Suppliers and Applications took a Notes edit and nothing else, Domains
 * and Network were read-only, and People and Devices could only be filled by
 * CSV import. A register nobody can add a row to is a register that stays
 * empty, which is the failure §17 names as the one that kills this.
 *
 * One drawer rather than six forms. Each register describes its own fields in
 * lib/registerFields.ts and the renderer from the record types engine draws
 * them, so the built-in registers and the custom ones now edit through exactly
 * the same code — and a fix to a date input is a fix everywhere.
 */

import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useOnline } from '../../hooks/useOnline';
import { Button, Drawer } from '../ui';
import { FieldInput, missingRequired, type FieldLookups, type RenderableField } from './RecordField';
import s from './screens.module.scss';

export interface EditDrawerProps<T> {
  open: boolean;
  /** The record being edited, or null when adding. */
  record: T | null;
  /** What a new record starts as.
   *
   *  For a screen that already knows something about what is being added — the
   *  printers screen knows it is adding a printer — so the type does not have to
   *  be chosen before the form will show the fields that depend on it. */
  initial?: Record<string, unknown>;
  fields: RenderableField[];
  lookups: FieldLookups;
  /** Singular, lower case — "supplier", "domain". Used in the title and prompts. */
  noun: string;
  icon: string;
  title?: string;
  meta?: ReactNode;
  /** Extra content under the form — a warning, an explanation. */
  children?: ReactNode;
  onClose: () => void;
  onSave: (values: Record<string, unknown>) => void;
  onDelete?: () => void;
  saving?: boolean;
  deleting?: boolean;
  error?: unknown;
}

/** Pulls the editable values out of a record, keyed by field id.
 *
 *  Field ids are the model's own property names, so this is a filtered copy
 *  rather than a mapping — which is the point: no translation layer means no
 *  place for a field to go missing between the form and the record.
 */
function valuesOf<T>(record: T | null, fields: RenderableField[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!record) return out;
  for (const f of fields) {
    const v = (record as Record<string, unknown>)[f.id];
    if (v !== undefined) out[f.id] = v;
  }
  return out;
}

export function EditDrawer<T>({
  open,
  record,
  initial,
  fields,
  lookups,
  noun,
  icon,
  title,
  meta,
  children,
  onClose,
  onSave,
  onDelete,
  saving = false,
  deleting = false,
  error,
}: EditDrawerProps<T>) {
  const online = useOnline();
  const [values, setValues] = useState<Record<string, unknown>>({});

  /* Reset on open, not on mount. The drawer stays mounted between records, so
     without this the second thing you open shows the first one's values. */
  useEffect(() => {
    if (open) setValues(record ? valuesOf(record, fields) : { ...initial });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record]);

  /* What is actually on screen. Sections, required-field checks and the
     headings all have to agree about this, and they only do if it is worked out
     once. */
  const visible = fields.filter((field) => !field.hiddenWhen?.(values));

  /* Against the visible fields, so a hidden required one cannot block a save
     nobody can see how to unblock — a hired printer with no purchase date is
     not an incomplete form, it is a printer. */
  const missing = missingRequired(visible, values);
  const isNew = record === null;

  return (
    <Drawer
      open={open}
      title={title ?? (isNew ? `New ${noun}` : `Edit ${noun}`)}
      sub={isNew ? undefined : String((record as Record<string, unknown>)?.name ?? '')}
      avatar={<i className={`ti ${icon}`} aria-hidden />}
      meta={meta}
      onClose={onClose}
      footer={
        <div className={s.actions}>
          {/* In the footer, not at the foot of the form.
              A fifteen-field form scrolls, so a save that failed reported it
              somewhere below the fold while the button sat still in view. From
              where anybody is actually looking, that is a button that does
              nothing. */}
          {error !== undefined && error !== null && (
            <div className={`${s.ownedNote} ${s.saveError}`}>
              <i className="ti ti-alert-triangle" aria-hidden />
              <span>{error instanceof Error ? error.message : `Could not save the ${noun}.`}</span>
            </div>
          )}
          <Button
            primary
            icon={saving ? 'ti-loader-2' : 'ti-device-floppy'}
            spinning={saving}
            disabled={!online || saving || missing.length > 0}
            onClick={() => onSave(values)}
          >
            {saving ? 'Saving…' : isNew ? `Add ${noun}` : 'Save changes'}
          </Button>

          {!isNew && onDelete && (
            <Button
              icon={deleting ? 'ti-loader-2' : 'ti-trash'}
              spinning={deleting}
              disabled={!online || deleting}
              onClick={() => {
                const label = String((record as Record<string, unknown>)?.name ?? `this ${noun}`);
                if (window.confirm(`Delete "${label}"? This cannot be undone.`)) onDelete();
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}

          {/* Writes online only, by design (§7 rule: reads offline, writes online).
              No sync queue and no conflict resolution — a genuinely hard problem
              this app does not have. */}
          {!online && <div className={s.hint}>Editing is disabled while offline.</div>}
        </div>
      }
    >
      {visible.map((field, i) => (
        <Fragment key={field.id}>
          {/* Compared against the previous *visible* field, not the previous
              defined one. Comparing against the full list was the bug: with
              hidden fields the two lists have different indexes, so a heading
              was measured against a field that is not on screen and printed
              again and again. Worst on a printer, which hides the most. */}
          {field.section && field.section !== visible[i - 1]?.section && (
            <div className={s.sectionLabel}>{field.section}</div>
          )}
          <FieldInput
            field={field}
            value={values[field.id]}
            onChange={(v) => setValues((prev) => ({ ...prev, [field.id]: v }))}
            lookups={lookups}
            values={values}
          />
        </Fragment>
      ))}

      {children}

      {missing.length > 0 && (
        <div className={s.ownedNote}>
          <i className="ti ti-alert-triangle" aria-hidden />
          <span>
            {missing.map((f) => f.label).join(' and ')} {missing.length === 1 ? 'is' : 'are'}{' '}
            required.
          </span>
        </div>
      )}

    </Drawer>
  );
}

/** The badge shown on a register whose columns the nightly sync owns. */
export function SyncOwnedNote({ what }: { what: string }) {
  return (
    <div className={s.ownedNote}>
      <i className="ti ti-refresh" aria-hidden />
      <span>
        {what} is written by the nightly Microsoft 365 sync. Anything you set here is a stopgap
        until the sync next runs, which will overwrite it — Notes is the exception and is yours.
      </span>
    </div>
  );
}
